import React from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { TableRow, TableCell } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '../ui/Checkbox'
import { Bot } from 'lucide-react'

interface AdvisorAssignmentSectionProps {
  advisors: Array<{
    chatbotid: string
    chatbotname: string
  }>
  teams: Array<{
    id: number
    name: string
  }>
  isAdvisorAssignedToTeam: (advisorId: string, teamId: number) => boolean
  onAdvisorToggle: (teamId: number, advisorId: string) => void
  isMobile?: boolean
  isReadOnly?: boolean
}

const AdvisorAssignmentSection: React.FC<AdvisorAssignmentSectionProps> =
  React.memo(
    ({
      advisors,
      teams,
      isAdvisorAssignedToTeam,
      onAdvisorToggle,
      isMobile = false,
      isReadOnly = false,
    }) => {
      if (isMobile) {
        return (
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <span className='text-blue-600 text-lg'>
                <Bot />
              </span>
              <h3 className='text-lg font-semibold text-blue-700'>Advisors</h3>
            </div>

            {advisors.length > 0 ? (
              <div className='space-y-4'>
                {advisors.map((advisor) => (
                  <Card
                    key={advisor.chatbotid}
                    className='hover:shadow-md transition-shadow'
                  >
                    <CardHeader className='pb-3'>
                      <h4 className='font-semibold text-gray-800'>
                        {advisor.chatbotname}
                      </h4>
                    </CardHeader>
                    <CardContent>
                      <div
                        className={`grid gap-3 ${
                          teams.length === 1
                            ? 'grid-cols-1'
                            : teams.length === 2
                              ? 'grid-cols-2'
                              : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
                        }`}
                      >
                        {teams.map((team) => (
                          <div
                            key={team.id}
                            className='flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors'
                          >
                            <span
                              className='font-medium text-sm text-gray-700 truncate mr-2'
                              title={team.name}
                            >
                              {team.name}
                            </span>
                            <Checkbox
                              checked={isAdvisorAssignedToTeam(
                                advisor.chatbotid,
                                team.id
                              )}
                              onChange={() =>
                                onAdvisorToggle(team.id, advisor.chatbotid)
                              }
                              disabled={isReadOnly}
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Alert className='border-dashed border-blue-200 bg-blue-50'>
                <span className='text-blue-600 text-lg'>
                  <Bot />
                </span>
                <AlertDescription className='text-center space-y-2'>
                  <h4 className='font-semibold text-blue-800'>
                    No advisors available
                  </h4>
                  <p className='text-sm text-blue-600'>
                    You need to create advisor chatbots before you can assign
                    them to teams.
                  </p>
                  <p className='text-xs text-blue-500'>
                    Visit the chatbot configuration section to set up your first
                    advisor.
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )
      }

      return (
        <>
          <TableRow className='bg-sky-50 border-t-2 border-blue-200'>
            <TableCell className='p-4 font-semibold text-dark-blue border-r bg-gradient-to-r from-blue-50 to-sky-50 sticky left-0 z-10'>
              <div className='flex items-center gap-2'>
                <span className='text-blue-600 text-lg'>
                  <Bot />
                </span>
                <span>Advisors</span>
              </div>
            </TableCell>
            {teams.map((team) => (
              <TableCell
                key={team.id}
                className='p-4 bg-gradient-to-r from-blue-50 to-sky-50 whitespace-nowrap'
              />
            ))}
          </TableRow>

          {advisors.length > 0 ? (
            advisors.map((advisor, index) => (
              <TableRow
                key={advisor.chatbotid}
                className={`border-t hover:bg-blue-25 transition-colors ${
                  index === advisors.length - 1
                    ? 'border-b-2 border-blue-200'
                    : ''
                }`}
              >
                <TableCell className='p-4 border-r sticky left-0 z-10 bg-white'>
                  <div
                    className='font-medium text-gray-800 truncate'
                    title={advisor.chatbotname}
                  >
                    {advisor.chatbotname}
                  </div>
                </TableCell>
                {teams.map((team) => (
                  <TableCell
                    key={team.id}
                    className='p-4 text-center align-middle whitespace-nowrap'
                  >
                    <div className='flex justify-center items-center'>
                      <Checkbox
                        checked={isAdvisorAssignedToTeam(
                          advisor.chatbotid,
                          team.id
                        )}
                        onChange={() =>
                          onAdvisorToggle(team.id, advisor.chatbotid)
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow className='border-t border-b-2 border-blue-200'>
              <TableCell
                className='p-6 border-r sticky left-0 z-10 bg-white'
                colSpan={teams.length + 1}
              >
                <div className='flex flex-col items-center text-center text-gray-500 space-y-2'>
                  <span className='text-blue-600 text-lg'>
                    <Bot />
                  </span>
                  <div className='text-sm'>
                    <p className='font-medium text-gray-600 mb-1'>
                      No advisors available
                    </p>
                    <p className='text-xs text-gray-500'>
                      Create advisor chatbots first to assign them to teams
                    </p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </>
      )
    }
  )

AdvisorAssignmentSection.displayName = 'AdvisorAssignmentSection'

export { AdvisorAssignmentSection }
